import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../context/PermissionContext';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  InputAdornment,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogActions,
  Checkbox,
  Collapse,
} from '@mui/material';
import {
  Search,
  Refresh,
  Download,
  Upload,
  OpenInNew,
  FilterList,
  FileDownload,
  Image as ImageIcon,
  Videocam,
  Person,
  ContentCopy,
  ChevronLeft,
  ChevronRight,
  Close,
  PlayArrow,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Email,
  Star,
  StarBorder,
  Code,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { AdminGuard } from '../../components/PermissionGuard';
import API_BASE_URL from '../../config/api';
import * as XLSX from 'xlsx';

// Hard list of valid /dashboard/* route slugs (manually mirrored from
// App.js). Used as a sanity check — if a resolver produces a slug NOT
// in this set, the cell is rendered as plain text instead of a dead
// link. Keep this in sync with App.js whenever a route is added or
// commented out.
const VALID_DASHBOARD_ROUTES = new Set([
  '48-state-freight',
  'ai-image-generator',
  'ai-photo-pipeline',
  'ai-tools',
  'ai-video-generator',
  'ama9999-ucc-labels',
  'amazon-bulk-image-uploader',
  'amazon-di-ucc-label',
  'amazon-rug-titles',
  'asset-explorer',
  'aws-file-links',
  'aws-file-links-alternate',
  'barcode-generator',
  'bilingual-labels',
  'broken-link-checker',
  'bulk-image-downloader',
  'cad-invoices',
  'carton-labels',
  'collection-image-links',
  'customer-style-search',
  'document-translation',
  'email-campaign-builder',
  'excel-export-item-images',
  'excel-reports',
  'excel-tools',
  'expense-allocation-invoices',
  'file-converter',
  'home-goods-pdf',
  'item-image-grouping',
  'items-detail',
  'label-printing',
  'link-tools',
  'marketing-copies',
  'master-retail-rugs',
  'other-tools',
  'pallet-labels',
  'pdf-manipulation',
  'pdf-reports',
  'pdf-to-excel-ucc-labels',
  'pdf-tools',
  'pdq-full-pallet-pull-label',
  'po-distribution',
  'po-distribution-whs',
  'price-scraper',
  'product-data-validator',
  'product-details',
  'remove-background',
  'analytics-tools',
  'saf1909-inventory-analysis',
  'sales-dashboard',
  'sales-orders-export',
  'sales-performance-dashboard',
  'seo-content-writer',
  'social-media-content',
]);

// Display-name → slug overrides for file-history records. The literal
// label stored in `user_file_history.interface` is whatever each
// controller passed to InsertUserFileHistory — often a free-form
// description ("AWS Image Links Generation", "AIImageGenerator", "PDQ
// TBC Labels") rather than the canonical InterfaceName. Each entry was
// verified against App.js so it points at a real route. Disabled
// controllers (Jeff variants, FTP Transfer, etc.) are deliberately
// absent — those should fall through and render as plain text.
const INTERFACE_NAME_ALIASES = {
  // AI
  'aiimagegenerator': 'ai-image-generator',
  'ai image generator': 'ai-image-generator',
  'ai video generator': 'ai-video-generator',
  'ai photo pipeline': 'ai-photo-pipeline',
  'seo content writer': 'seo-content-writer',
  'amazon rug titles': 'amazon-rug-titles',
  // AWSImageLinksController — main "AWS File Links" tool
  'aws image links': 'aws-file-links',
  'aws image links generation': 'aws-file-links',
  'aws image links from excel': 'aws-file-links',
  'aws file links generation': 'aws-file-links',
  'aws file links': 'aws-file-links',
  // AWSImageLinksJeff* + AWSImageLinksAlternate — all surface as the
  // "AWS File Links Alternate" UI in production. The Rugs/Furniture
  // suffixes are written by the rugs-mode and furniture-mode flows of
  // AWSImageLinksAlternate.
  'aws file links alternate': 'aws-file-links-alternate',
  'aws file links alternate rugs': 'aws-file-links-alternate',
  'aws file links alternate furniture': 'aws-file-links-alternate',
  'aws image links jeff': 'aws-file-links-alternate',
  'aws image links jeff generation': 'aws-file-links-alternate',
  'aws image links jeff furniture': 'aws-file-links-alternate',
  'aws image links jeff furniture generation': 'aws-file-links-alternate',
  'jeff furniture': 'aws-file-links-alternate',
  'collection image links': 'collection-image-links',
  'amazon bulk image uploader': 'amazon-bulk-image-uploader',
  'broken link checker': 'broken-link-checker',
  'asset explorer': 'asset-explorer',
  'bulk image downloader': 'bulk-image-downloader',
  // PDF / labels
  'amazon label separator': 'amazon-di-ucc-label',
  'amazon label separator download': 'amazon-di-ucc-label',
  'amazon dio ucc label': 'amazon-di-ucc-label',
  'amazon di ucc label': 'amazon-di-ucc-label',
  'ama9999 ucc labels': 'ama9999-ucc-labels',
  'eu-ucc pdf modifier': 'carton-labels',
  'eu-ucc pdf modifier - item': 'carton-labels',
  'carton labels': 'carton-labels',
  'back labels': 'bilingual-labels',
  'bilingual labels': 'bilingual-labels',
  'wal4000 pdf': 'pallet-labels',
  'wal4000 pdfs': 'pallet-labels',
  "wal4000 pdf's": 'pallet-labels',
  'pdf manipulation': 'pdf-manipulation',
  'pdf to excel': 'pdf-to-excel-ucc-labels',
  'pdf to excel (ucc labels)': 'pdf-to-excel-ucc-labels',
  'cad invoices': 'cad-invoices',
  'master retail rugs': 'master-retail-rugs',
  'home goods pdf': 'home-goods-pdf',
  'expense allocation invoice': 'expense-allocation-invoices',
  'expense allocation invoices': 'expense-allocation-invoices',
  // LabelPrintingController flows — all surface as one route
  'label printing': 'label-printing',
  'custom rug label generation': 'label-printing',
  'custom rug template': 'label-printing',
  'upc/qr label generation': 'label-printing',
  'upc/qr labels template': 'label-printing',
  // Excel
  'export item images': 'excel-export-item-images',
  'export images': 'excel-export-item-images',
  'export images download': 'excel-export-item-images',
  'export images template': 'excel-export-item-images',
  'excel export item images': 'excel-export-item-images',
  'reports manager': 'excel-reports',
  'excel reports manager': 'excel-reports',
  'excel reports': 'excel-reports',
  'pdf reports': 'pdf-reports',
  'po distribution': 'po-distribution',
  'po distribution whs': 'po-distribution-whs',
  'po distribution whs generation': 'po-distribution-whs',
  'sales order creation': 'sales-orders-export',
  'sales orders export': 'sales-orders-export',
  'items detail': 'items-detail',
  'items detail export': 'items-detail',
  'general imports': 'items-detail',
  'item image grouping': 'item-image-grouping',
  // Other / customer / search
  'customer styles': 'customer-style-search',
  'customer styles search': 'customer-style-search',
  'customer styles search with details': 'customer-style-search',
  'csl data check': 'customer-style-search',
  'customer style search': 'customer-style-search',
  'document translation': 'document-translation',
  'product details': 'product-details',
  'product details & style detection': 'product-details',
  'product data validator': 'product-data-validator',
  'remove background': 'remove-background',
  'removebackground': 'remove-background',
  'state freight': '48-state-freight',
  '48 state freight': '48-state-freight',
  'price scraper': 'price-scraper',
  'price scraper results': 'price-scraper',
  'marketing copies': 'marketing-copies',
  'marketing copies generation': 'marketing-copies',
  'marketing copy generator': 'marketing-copies',
  'social media content': 'social-media-content',
  'social media content generation': 'social-media-content',
  'social media content generator': 'social-media-content',
  'email campaign builder': 'email-campaign-builder',
  'barcode generator': 'barcode-generator',
  'file converter': 'file-converter',
  'sales dashboard': 'sales-dashboard',
};

// Prefix-match rules — variable-middle controller patterns. Each rule is
// `[regex, slug]`. Patterns are anchored and intentionally narrow so
// "AWS Image Links Jeff" / "FTP Transfer" / etc. don't accidentally
// resolve to the wrong tool.
const INTERFACE_PREFIX_ALIASES = [
  // PDQLabelsController writes "PDQ {labelTypePrefix} Labels"
  [/^pdq\s+\S+\s+labels?$/i, 'pdq-full-pallet-pull-label'],
  // AWSImageLinksJeff* / AWSImageLinksJeffFurniture* — surface as the
  // "AWS File Links Alternate" UI. Match Jeff/Furniture FIRST so the
  // generic AWS rule below doesn't claim them.
  [/^aws\s+(image|file)\s+links\s+(jeff|furniture)(\s+\S+)*$/i, 'aws-file-links-alternate'],
  // AWSImageLinksController main tool — only matches when there's no
  // Jeff/Furniture qualifier (the rule above would have caught those).
  [/^aws\s+(image|file)\s+links(\s+(generation|from\s+excel))?$/i, 'aws-file-links'],
  // Customer Styles * — same controller
  [/^customer\s+styles?(\s+search)?(\s+with\s+details)?$/i, 'customer-style-search'],
  // Export Images * — same controller
  [/^export\s+images(\s+(download|template))?$/i, 'excel-export-item-images'],
];

// Compact label + color for the Gemini image-model badge shown on each AI
// Image row. Older rows pre-dating the Model column will have model === null.
const aiModelLabel = (model) => {
  if (!model) return 'Unknown';
  // Stable ids since 2026-10-10; older rows carry the -preview ids they ran with.
  if (model === 'gemini-3-pro-image' || model === 'gemini-3-pro-image-preview')         return 'Pro';
  if (model === 'gemini-3.1-flash-image' || model === 'gemini-3.1-flash-image-preview') return 'Flash';
  if (model === 'gemini-nano-banana-2.1')         return 'Nano Banana 2.1';
  return model;
};
const aiModelColor = (model) => {
  if (!model)                                       return '#94A3B8'; // slate (unknown)
  if (model === 'gemini-3-pro-image' || model === 'gemini-3-pro-image-preview')         return '#D97706'; // amber (premium tier)
  if (model === 'gemini-3.1-flash-image' || model === 'gemini-3.1-flash-image-preview') return '#0EA5E9'; // sky (fast/cheap)
  if (model === 'gemini-nano-banana-2.1')           return '#16A34A'; // green (newest, cheapest)
  return '#475569';
};

// Model that produced an AI video. Rows from before the Engine column existed are
// NULL — those were all Veo generations.
const videoModelLabel = (engine) => {
  const e = String(engine || 'veo').toLowerCase();
  if (e === 'omni') return 'Gemini Omni 1.1 Flash';
  if (e === 'seedance') return 'Seedance 2.0';
  if (e === 'veo-fast') return 'Veo 3.1 Fast';
  if (e === 'veo-lite') return 'Veo 3.1 Lite';
  return 'Veo 3.1';
};
const videoModelColor = (engine) => {
  const e = String(engine || 'veo').toLowerCase();
  return e === 'omni' ? '#7b1fa2' : e === 'seedance' ? '#00838f' : '#1565c0';
};

const FileHistoryAdmin = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { permissions: accessibleInterfaces } = usePermissions();

  // Build a name-keyed lookup of `{ name → path }` from the canonical
  // Interfaces table (delivered via /api/AccessControl/my-access). For
  // admins/owners this includes every active interface, so it's the
  // most reliable source of truth for the route slug. Lowercased keys
  // because user-typed names in older records are case-inconsistent.
  const interfaceNameToPath = useMemo(() => {
    const map = new Map();
    for (const i of accessibleInterfaces || []) {
      if (i.interfaceName) map.set(i.interfaceName.trim().toLowerCase(), i.path || `/dashboard/${i.interfaceKey}`);
      if (i.interfaceKey) map.set(i.interfaceKey.trim().toLowerCase(), i.path || `/dashboard/${i.interfaceKey}`);
    }
    return map;
  }, [accessibleInterfaces]);

  // Returns a /dashboard/<slug> path ONLY if the resolved slug maps to a
  // route that actually exists in App.js. If we can't be sure, returns
  // null and the caller renders the cell as plain text — this is the
  // explicit guard against e.g. "AWS Image Links Jeff" silently routing
  // to /dashboard/aws-file-links (a different tool).
  const resolveInterfacePath = useCallback((name) => {
    if (!name) return null;
    const trimmed = name.trim();
    const key = trimmed.toLowerCase();

    const candidate =
      // 1. Hardcoded alias (covers controller-side label strings)
      INTERFACE_NAME_ALIASES[key] ||
      // 2. Prefix patterns (variable-middle labels)
      (INTERFACE_PREFIX_ALIASES.find(([pat]) => pat.test(trimmed)) || [])[1] ||
      // 3. Canonical Interfaces table from /api/AccessControl/my-access
      (() => {
        const fromDb = interfaceNameToPath.get(key);
        if (!fromDb) return null;
        return fromDb.startsWith('/dashboard/') ? fromDb.slice('/dashboard/'.length) : fromDb;
      })() ||
      // 4. CamelCase-aware slugifier — last-resort guess
      (() => {
        const split = trimmed
          .replace(/([a-z\d])([A-Z])/g, '$1-$2')
          .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2');
        return split
          .toLowerCase()
          .replace(/[()&]/g, '')
          .replace(/[\/_]+/g, '-')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      })();

    if (!candidate) return null;
    // Sanity gate: only return a path if the slug matches a real route.
    // Anything else (disabled tool, typo, brand-new label nobody mapped)
    // falls through and the cell stays non-clickable.
    return VALID_DASHBOARD_ROUTES.has(candidate) ? `/dashboard/${candidate}` : null;
  }, [interfaceNameToPath]);

  const [history, setHistory] = useState([]);
  const [batchImages, setBatchImages] = useState({});
  const [expandedBatchRows, setExpandedBatchRows] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [searchEmail, setSearchEmail] = useState('');
  const [searchInterface, setSearchInterface] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [debouncedInterface, setDebouncedInterface] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'upload', 'download'

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // AI Images state
  const [aiImages, setAiImages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTotal, setAiTotal] = useState(0);
  const [aiPage, setAiPage] = useState(0);
  const [aiRowsPerPage, setAiRowsPerPage] = useState(25);
  const [aiSearchEmail, setAiSearchEmail] = useState('');
  const [aiDebouncedEmail, setAiDebouncedEmail] = useState('');
  const [aiMode, setAiMode] = useState('');
  const [aiPromptSearch, setAiPromptSearch] = useState('');
  const [aiDebouncedPrompt, setAiDebouncedPrompt] = useState('');
  const [aiFilenameSearch, setAiFilenameSearch] = useState('');
  const [aiDebouncedFilename, setAiDebouncedFilename] = useState('');
  const [aiDateFrom, setAiDateFrom] = useState('');
  const [aiDateTo, setAiDateTo] = useState('');
  const [aiSortBy, setAiSortBy] = useState('newest');
  const [lightboxIndex, setLightboxIndex] = useState(-1); // -1 = closed
  const [copiedId, setCopiedId] = useState(null);
  const [adminZoomActive, setAdminZoomActive] = useState(false);

  // AI Videos state
  const [aiVideos, setAiVideos] = useState([]);
  const [aiVideoLoading, setAiVideoLoading] = useState(false);
  const [aiVideoTotal, setAiVideoTotal] = useState(0);
  const [aiVideoPage, setAiVideoPage] = useState(0);
  const [aiVideoRowsPerPage, setAiVideoRowsPerPage] = useState(25);
  const [aiVideoSearchEmail, setAiVideoSearchEmail] = useState('');
  const [aiVideoPromptSearch, setAiVideoPromptSearch] = useState('');
  const [aiVideoDateFrom, setAiVideoDateFrom] = useState('');
  const [aiVideoDateTo, setAiVideoDateTo] = useState('');
  const [aiVideoSortBy, setAiVideoSortBy] = useState('newest');
  const [playingVideoIndex, setPlayingVideoIndex] = useState(-1);

  // Email campaigns state (tab 3)
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [emailTemplatesTotal, setEmailTemplatesTotal] = useState(0);
  const [emailTemplatesPage, setEmailTemplatesPage] = useState(0);
  const [emailTemplatesRowsPerPage, setEmailTemplatesRowsPerPage] = useState(25);
  const [emailTemplateSearchEmail, setEmailTemplateSearchEmail] = useState('');
  const [emailTemplateSearch, setEmailTemplateSearch] = useState('');
  const [emailTemplateCampaignType, setEmailTemplateCampaignType] = useState('');
  const [emailTemplateDateFrom, setEmailTemplateDateFrom] = useState('');
  const [emailTemplateDateTo, setEmailTemplateDateTo] = useState('');
  const [emailTemplatePreview, setEmailTemplatePreview] = useState(null); // { id, name, html, mjml, ... }
  const [emailTemplatePreviewMode, setEmailTemplatePreviewMode] = useState('preview'); // 'preview' | 'mjml' | 'html'
  const [emailTemplatePreviewIndex, setEmailTemplatePreviewIndex] = useState(-1);

  // Bulk download selection (file history tab)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // AI images selection
  const [aiSelectMode, setAiSelectMode] = useState(false);
  const [selectedAiIds, setSelectedAiIds] = useState(new Set());
  const [aiCompareDialogOpen, setAiCompareDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Resizable columns
  const [fileNameWidth, setFileNameWidth] = useState(250);
  const [interfaceWidth, setInterfaceWidth] = useState(150);
  const resizing = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing.current) return;
      const { setter, startX, startWidth } = resizing.current;
      const newWidth = Math.max(80, startWidth + (e.clientX - startX));
      setter(newWidth);
    };
    const handleMouseUp = () => { resizing.current = null; document.body.style.cursor = ''; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, []);

  const startResize = (e, setter, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { setter, startX: e.clientX, startWidth: currentWidth };
    document.body.style.cursor = 'col-resize';
  };

  const resizeHandleSx = {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.3) },
    zIndex: 1,
  };

  // Debounce text inputs to avoid API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmail(searchEmail), 400);
    return () => clearTimeout(timer);
  }, [searchEmail]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInterface(searchInterface), 400);
    return () => clearTimeout(timer);
  }, [searchInterface]);

  // Reset to page 0 whenever any filter changes
  useEffect(() => {
    setPage(0);
  }, [debouncedEmail, debouncedInterface, dateFrom, dateTo, filterType]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });

      if (debouncedEmail) params.set('userEmail', debouncedEmail);
      if (debouncedInterface) params.set('interfaceName', debouncedInterface);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (filterType !== 'all') params.set('uploadType', filterType);

      const response = await fetch(
        `${API_BASE_URL}/api/Users/allFileHistory?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        let items = data.history || [];
        setHistory(items);
        setTotal(data.total || 0);
        setBatchImages(data.batchImages || {});
      } else {
        setError(data.message || 'Failed to fetch file history');
      }
    } catch (err) {
      setError('Failed to fetch file history');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedEmail, debouncedInterface, dateFrom, dateTo, filterType]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSearch = () => {
    setDebouncedEmail(searchEmail);
    setDebouncedInterface(searchInterface);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchEmail('');
    setSearchInterface('');
    setDebouncedEmail('');
    setDebouncedInterface('');
    setDateFrom('');
    setDateTo('');
    setFilterType('all');
    setPage(0);
  };

  // Clear selection when page, filters, or data change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, rowsPerPage, debouncedEmail, debouncedInterface, dateFrom, dateTo, filterType]);

  // Selection helpers — selectedIds may contain numeric h.id values for normal rows
  // and string IDs of the form "batch:{batchId}:{index}" for individual generated
  // images inside a batch row.
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Build the list of every selectable thing on the current page: normal rows AND
  // every individual generated image inside a batch row.
  const selectableOnPage = (() => {
    const out = [];
    for (const h of history) {
      if (h.link && !h.link.startsWith('batch:')) {
        out.push(h.id);
      } else if (h.link && h.link.startsWith('batch:')) {
        const batchId = h.link.replace('batch:', '');
        const list = batchImages[batchId] || [];
        list.forEach((_, i) => out.push(`batch:${batchId}:${i}`));
      }
    }
    return out;
  })();
  const isAllSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableOnPage.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableOnPage.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  // Toggle every sub-image of a batch row at once (used by the main row checkbox
  // when the row is a batch row).
  const toggleBatchAll = (batchId) => {
    const list = batchImages[batchId] || [];
    if (list.length === 0) return;
    const ids = list.map((_, i) => `batch:${batchId}:${i}`);
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  // Resolve any mix of numeric and synthetic batch IDs to a uniform list of
  // { link, filename, interfaceName, userEmailId, createdAt, upload, typeOfFile }
  // items the download/export/compare flows can consume.
  const resolveSelectedItems = () => {
    const items = [];
    for (const id of selectedIds) {
      if (typeof id === 'string' && id.startsWith('batch:')) {
        // batch:{batchId}:{index}  — batchId can itself contain colons, so parse
        // the trailing index off the end.
        const lastColon = id.lastIndexOf(':');
        if (lastColon < 6) continue;
        const batchId = id.substring('batch:'.length, lastColon);
        const idx = parseInt(id.substring(lastColon + 1), 10);
        const list = batchImages[batchId];
        if (!list || !list[idx]) continue;
        const parent = history.find((h) => h.link === `batch:${batchId}`);
        items.push({
          id,
          link: list[idx].s3Url,
          filename: list[idx].fileName,
          interfaceName: parent?.interfaceName || '',
          userEmailId: parent?.userEmailId || '',
          createdAt: parent?.createdAt || '',
          upload: parent?.upload || false,
          typeOfFile: parent?.typeOfFile || '',
        });
      } else {
        const h = history.find((x) => x.id === id);
        if (h && h.link && !h.link.startsWith('batch:')) items.push(h);
      }
    }
    return items;
  };

  const forceDownload = async (url, filename) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const proxyUrl = `${API_BASE_URL}/api/Users/downloadFile?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename || '')}`;
      const res = await fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || url.split('/').pop()?.split('?')[0] || 'download';
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  const handleBulkDownload = async () => {
    const items = resolveSelectedItems();
    if (items.length === 0) return;

    for (let i = 0; i < items.length; i++) {
      await forceDownload(items[i].link, items[i].filename);
      if (i < items.length - 1) await new Promise(r => setTimeout(r, 300));
    }

    setSelectedIds(new Set());
  };

  const isImageFile = (h) => {
    if (!h.link || h.link.startsWith('batch:')) return false;
    const ext = (h.filename || h.link || '').toLowerCase();
    return /\.(png|jpg|jpeg|gif|webp|bmp|svg)/.test(ext) || (h.typeOfFile && /^(png|jpg|jpeg|gif|webp|image)$/i.test(h.typeOfFile));
  };

  const getCompareImages = () => {
    return resolveSelectedItems().filter((h) => isImageFile(h));
  };

  // AI image selection helpers
  const toggleAiSelect = (id) => {
    setSelectedAiIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const isAllAiSelected = aiImages.length > 0 && aiImages.every(img => selectedAiIds.has(img.id));
  const toggleAiSelectAll = () => {
    if (isAllAiSelected) {
      setSelectedAiIds(new Set());
    } else {
      setSelectedAiIds(new Set(aiImages.map(img => img.id)));
    }
  };
  const getSelectedAiImages = () => aiImages.filter(img => selectedAiIds.has(img.id));
  const handleAiBulkDownload = async () => {
    const imgs = getSelectedAiImages();
    if (imgs.length === 0) return;
    const token = localStorage.getItem('jwtToken');

    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      const downloadUrl = `${API_BASE_URL}/api/AIImageGenerator/admin/download-image/${img.id}`;
      try {
        const res = await fetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = img.fileName || `ai_image_${img.id}.png`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(img.s3Url || getImageUrl(img.id), '_blank');
      }
      if (i < imgs.length - 1) await new Promise(r => setTimeout(r, 300));
    }

    setSelectedAiIds(new Set());
  };

  const handleExportExcel = async () => {
    // If rows are selected, export only those; otherwise fetch all matching records.
    // For selected exports we use the unified resolver so individual batch sub-images
    // are exported as their own rows with the real S3 URL.
    const hasSelection = selectedIds.size > 0;
    let allHistory;
    let allBatchImages = batchImages;

    if (hasSelection) {
      // Resolved items already carry the real link/filename; treat them as plain rows
      // in the export pipeline below by giving them a non-batch link.
      allHistory = resolveSelectedItems();
    } else {
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({ exportAll: 'true' });
      if (searchEmail) params.set('userEmail', searchEmail);
      if (searchInterface) params.set('interfaceName', searchInterface);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (filterType !== 'all') params.set('uploadType', filterType);

      allHistory = history;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/Users/allFileHistory?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        const data = await res.json();
        if (data.success) {
          allHistory = data.history || [];
          allBatchImages = data.batchImages || {};
        }
      } catch (err) {
        console.error('Failed to fetch all records for export, using current page:', err);
      }
    }

    if (allHistory.length === 0) return;

    const rows = [];

    for (const h of allHistory) {
      if (h.link && h.link.startsWith('batch:')) {
        const batchId = h.link.replace('batch:', '');
        const images = allBatchImages[batchId];
        if (images && images.length > 0) {
          images.forEach((img, i) => {
            rows.push({
              'User Email': h.userEmailId,
              'File Name': img.fileName || `${h.filename} (${i + 1})`,
              'Interface': h.interfaceName,
              'Type': h.upload ? 'Download' : 'Upload',
              'File Type': h.typeOfFile,
              'Date': h.createdAt,
              'S3 Link': img.s3Url,
            });
          });
          continue;
        }
      }
      rows.push({
        'User Email': h.userEmailId,
        'File Name': h.filename,
        'Interface': h.interfaceName,
        'Type': h.upload ? 'Download' : 'Upload',
        'File Type': h.typeOfFile,
        'Date': h.createdAt,
        'S3 Link': h.link,
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);

    // Make S3 links clickable
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: 6 })];
      if (cell && cell.v && typeof cell.v === 'string' && cell.v.startsWith('http')) {
        cell.l = { Target: cell.v };
      }
    }

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, { wch: 40 }, { wch: 25 }, { wch: 10 },
      { wch: 10 }, { wch: 20 }, { wch: 60 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'File History');
    const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(wb, `all_file_history_${dt}.xlsx`);
  };

  // AI Images: debounce inputs
  useEffect(() => {
    const timer = setTimeout(() => setAiDebouncedEmail(aiSearchEmail), 400);
    return () => clearTimeout(timer);
  }, [aiSearchEmail]);

  useEffect(() => {
    const timer = setTimeout(() => setAiDebouncedPrompt(aiPromptSearch), 400);
    return () => clearTimeout(timer);
  }, [aiPromptSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setAiDebouncedFilename(aiFilenameSearch), 400);
    return () => clearTimeout(timer);
  }, [aiFilenameSearch]);

  // AI Images: reset page on filter change
  useEffect(() => {
    setAiPage(0);
  }, [aiDebouncedEmail, aiDebouncedPrompt, aiDebouncedFilename, aiMode, aiDateFrom, aiDateTo, aiSortBy]);

  // AI Images: fetch flat image list
  // Keep the first visible row in view when rows-per-page changes.
  // e.g. page 8 at 25/page (showing rows 200..224) + switch to 50/page ⇒ page 4 (rows 200..249).
  const computeNewPageAfterRowsChange = (currentPage, oldRows, newRows) =>
    newRows > 0 ? Math.floor((currentPage * oldRows) / newRows) : 0;

  // Track which tabs have been initially loaded so re-entering a tab shows
  // existing data instantly (no loading spinner) while a refresh happens in
  // the background. Dependencies (page, filters) still trigger a normal fetch.
  const loadedTabsRef = useRef({ images: false, videos: false, email: false });

  const fetchAiImages = useCallback(async (opts = {}) => {
    // Only show the full spinner on the first load of this tab.
    // On subsequent fetches (paging, filter changes, tab re-entry), keep the
    // old images visible so the UI doesn't flash an empty state.
    if (!loadedTabsRef.current.images) setAiLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({
        page: (aiPage + 1).toString(),
        pageSize: aiRowsPerPage.toString(),
        sortBy: aiSortBy,
      });
      if (aiDebouncedEmail) params.set('userEmail', aiDebouncedEmail);
      if (aiMode) params.set('mode', aiMode);
      if (aiDebouncedPrompt) params.set('prompt', aiDebouncedPrompt);
      if (aiDebouncedFilename) params.set('filename', aiDebouncedFilename);
      if (aiDateFrom) params.set('dateFrom', aiDateFrom);
      if (aiDateTo) params.set('dateTo', aiDateTo);

      const response = await fetch(
        `${API_BASE_URL}/api/AIImageGenerator/admin/images?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setAiImages(data.images || []);
        setAiTotal(data.total || 0);
        loadedTabsRef.current.images = true;
      }
    } catch (err) {
      console.error('Failed to fetch AI images:', err);
    } finally {
      setAiLoading(false);
    }
  }, [aiPage, aiRowsPerPage, aiDebouncedEmail, aiDebouncedPrompt, aiDebouncedFilename, aiMode, aiDateFrom, aiDateTo, aiSortBy]);

  useEffect(() => {
    if (activeTab === 1) fetchAiImages();
  }, [activeTab, fetchAiImages]);

  const fetchAiVideos = useCallback(async () => {
    if (!loadedTabsRef.current.videos) setAiVideoLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({
        page: (aiVideoPage + 1).toString(),
        pageSize: aiVideoRowsPerPage.toString(),
        sortBy: aiVideoSortBy,
      });
      if (aiVideoSearchEmail) params.set('userEmail', aiVideoSearchEmail);
      if (aiVideoPromptSearch) params.set('prompt', aiVideoPromptSearch);
      if (aiVideoDateFrom) params.set('dateFrom', aiVideoDateFrom);
      if (aiVideoDateTo) params.set('dateTo', aiVideoDateTo);

      const response = await fetch(
        `${API_BASE_URL}/api/AIVideoGenerator/admin/videos?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setAiVideos(data.videos || []);
        setAiVideoTotal(data.total || 0);
        loadedTabsRef.current.videos = true;
      }
    } catch (err) {
      console.error('Failed to fetch AI videos:', err);
    } finally {
      setAiVideoLoading(false);
    }
  }, [aiVideoPage, aiVideoRowsPerPage, aiVideoSearchEmail, aiVideoPromptSearch, aiVideoDateFrom, aiVideoDateTo, aiVideoSortBy]);

  useEffect(() => {
    if (activeTab === 2) fetchAiVideos();
  }, [activeTab, fetchAiVideos]);

  // --- Email Campaigns admin tab ---
  const fetchEmailTemplates = useCallback(async () => {
    if (!loadedTabsRef.current.email) setEmailTemplatesLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const params = new URLSearchParams({
        page: (emailTemplatesPage + 1).toString(),
        pageSize: emailTemplatesRowsPerPage.toString(),
      });
      if (emailTemplateSearchEmail) params.set('userEmail', emailTemplateSearchEmail);
      if (emailTemplateSearch) params.set('search', emailTemplateSearch);
      if (emailTemplateCampaignType) params.set('campaignType', emailTemplateCampaignType);
      if (emailTemplateDateFrom) params.set('dateFrom', emailTemplateDateFrom);
      if (emailTemplateDateTo) params.set('dateTo', emailTemplateDateTo);

      const response = await fetch(
        `${API_BASE_URL}/api/EmailCampaignBuilder/admin/templates?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setEmailTemplates(data.templates || []);
        setEmailTemplatesTotal(data.total || 0);
        loadedTabsRef.current.email = true;
      }
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    } finally {
      setEmailTemplatesLoading(false);
    }
  }, [emailTemplatesPage, emailTemplatesRowsPerPage, emailTemplateSearchEmail, emailTemplateSearch, emailTemplateCampaignType, emailTemplateDateFrom, emailTemplateDateTo]);

  useEffect(() => {
    if (activeTab === 3) fetchEmailTemplates();
  }, [activeTab, fetchEmailTemplates]);

  const openEmailTemplatePreview = async (templateId) => {
    const idx = emailTemplates.findIndex(t => t.id === templateId);
    if (idx >= 0) setEmailTemplatePreviewIndex(idx);
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await fetch(
        `${API_BASE_URL}/api/EmailCampaignBuilder/admin/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success && data.template) {
        setEmailTemplatePreview(data.template);
        setEmailTemplatePreviewMode('preview');
      }
    } catch (err) {
      console.error('Failed to fetch email template:', err);
    }
  };

  const previewPrevTemplate = () => {
    if (emailTemplates.length === 0) return;
    const nextIdx = emailTemplatePreviewIndex - 1;
    if (nextIdx < 0) return;
    openEmailTemplatePreview(emailTemplates[nextIdx].id);
  };
  const previewNextTemplate = () => {
    if (emailTemplates.length === 0) return;
    const nextIdx = emailTemplatePreviewIndex + 1;
    if (nextIdx >= emailTemplates.length) return;
    openEmailTemplatePreview(emailTemplates[nextIdx].id);
  };

  // Keyboard handler attached directly to the Dialog (via onKeyDown below)
  // so it fires reliably inside MUI's focus trap. Kept as a function so we
  // always read the latest state closure.
  const handleEmailPreviewKeyDown = (e) => {
    if (!emailTemplatePreview) return;
    const tag = (e.target?.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); previewNextTemplate(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); previewPrevTemplate(); }
  };

  const getImageUrl = (imageId) => {
    const token = localStorage.getItem('jwtToken');
    return `${API_BASE_URL}/api/AIImageGenerator/image/${imageId}?token=${token}`;
  };

  const handleCopyPrompt = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleViewBatch = (batchId) => {
    // Filter to show only this batch
    setAiPromptSearch('');
    setAiDebouncedPrompt('');
    setAiFilenameSearch('');
    setAiDebouncedFilename('');
    setAiSearchEmail('');
    setAiDebouncedEmail('');
    setAiMode('');
    setAiDateFrom('');
    setAiDateTo('');
    // We'll use a special state to filter by batch
    setAiImages(prev => prev.filter(img => img.batchId === batchId));
  };

  // Lightbox keyboard navigation
  useEffect(() => {
    if (lightboxIndex < 0) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) { setLightboxIndex(i => i - 1); setAdminZoomActive(false); }
      if (e.key === 'ArrowRight' && lightboxIndex < aiImages.length - 1) { setLightboxIndex(i => i + 1); setAdminZoomActive(false); }
      if (e.key === 'Escape') { setLightboxIndex(-1); setAdminZoomActive(false); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, aiImages.length]);

  // Touch-swipe for the AI-image lightbox on mobile. Mirrors the keyboard arrows:
  // swipe left = next, swipe right = previous. Ignored while zoom mode is active.
  const lightboxTouchRef = useRef(null);
  const handleLightboxTouchStart = (e) => {
    const t = e.changedTouches[0];
    lightboxTouchRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleLightboxTouchEnd = (e) => {
    const start = lightboxTouchRef.current;
    lightboxTouchRef.current = null;
    if (!start || adminZoomActive || aiImages.length <= 1) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && lightboxIndex < aiImages.length - 1) {
      setLightboxIndex(i => i + 1);
      setAdminZoomActive(false);
    } else if (dx > 0 && lightboxIndex > 0) {
      setLightboxIndex(i => i - 1);
      setAdminZoomActive(false);
    }
  };

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <AdminGuard>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          File History
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          View upload and download history for all users
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="File History" />
          <Tab icon={<ImageIcon sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label="AI Generated Images" />
          <Tab icon={<Videocam sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label="AI Generated Videos" />
          <Tab icon={<Email sx={{ fontSize: 18, mr: 0.5 }} />} iconPosition="start" label="Email Campaigns" />
        </Tabs>

        {activeTab === 1 && (
          <>
            {/* AI Images Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="User Email" fullWidth size="small" value={aiSearchEmail} onChange={(e) => setAiSearchEmail(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="Search Prompts" fullWidth size="small" value={aiPromptSearch} onChange={(e) => setAiPromptSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <TextField label="Search Filename" fullWidth size="small" value={aiFilenameSearch} onChange={(e) => setAiFilenameSearch(e.target.value)}
                    placeholder="e.g. ABT141A"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select value={aiMode} label="Mode" onChange={(e) => setAiMode(e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Rugs">Rugs</MenuItem>
                      <MenuItem value="Furniture">Furniture</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort</InputLabel>
                    <Select value={aiSortBy} label="Sort" onChange={(e) => setAiSortBy(e.target.value)}>
                      <MenuItem value="newest">Newest</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                      <MenuItem value="user">By User</MenuItem>
                      <MenuItem value="mode">By Mode</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <TextField label="From" type="date" fullWidth size="small" value={aiDateFrom} onChange={(e) => setAiDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& input::-webkit-calendar-picker-indicator': { filter: isDark ? 'invert(1)' : 'none' } }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <TextField label="To" type="date" fullWidth size="small" value={aiDateTo} onChange={(e) => setAiDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& input::-webkit-calendar-picker-indicator': { filter: isDark ? 'invert(1)' : 'none' } }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1}>
                  <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchAiImages} fullWidth>Refresh</Button>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                {[
                  { label: 'Today', days: 0 },
                  { label: '3 Days', days: 3 },
                  { label: '1 Week', days: 7 },
                  { label: '2 Weeks', days: 14 },
                  { label: '1 Month', days: 30 },
                  { label: '3 Months', days: 90 },
                  { label: 'All Time', days: -1 },
                ].map(({ label, days }) => {
                  const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const today = localDate(new Date());
                  const fromDate = new Date(); fromDate.setDate(fromDate.getDate() - days);
                  const from = days === -1 ? '' : days === 0 ? today : localDate(fromDate);
                  const to = days === -1 ? '' : today;
                  const isActive = days === -1 ? (!aiDateFrom && !aiDateTo) : (aiDateFrom === from && aiDateTo === to);
                  return (
                    <Chip key={label} label={label} size="small" clickable
                      onClick={() => { setAiDateFrom(from); setAiDateTo(to); }}
                      sx={{
                        height: 24, fontSize: '0.7rem', fontWeight: isActive ? 700 : 400,
                        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                        border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                      }}
                    />
                  );
                })}
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {aiTotal.toLocaleString()} images{aiSelectMode && selectedAiIds.size > 0 ? ` · ${selectedAiIds.size} selected` : ''}
                </Typography>
                <Button variant={aiSelectMode ? 'contained' : 'outlined'} size="small"
                  onClick={() => { setAiSelectMode(!aiSelectMode); setSelectedAiIds(new Set()); }}
                  sx={aiSelectMode
                    ? { bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark }, textTransform: 'none', fontSize: '0.75rem' }
                    : { borderColor: theme.palette.primary.main, color: theme.palette.primary.main, textTransform: 'none', fontSize: '0.75rem' }
                  }>
                  {aiSelectMode ? 'Cancel' : 'Select'}
                </Button>
                {aiSelectMode && (
                  <>
                    <Checkbox size="small" checked={isAllAiSelected} indeterminate={selectedAiIds.size > 0 && !isAllAiSelected} onChange={toggleAiSelectAll} sx={{ p: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">All</Typography>
                    {selectedAiIds.size > 0 && (
                      <>
                        <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleAiBulkDownload} color="primary" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                          Download ({selectedAiIds.size})
                        </Button>
                        {selectedAiIds.size >= 2 && selectedAiIds.size <= 8 && (
                          <Button variant="outlined" size="small" startIcon={<ImageIcon />} onClick={() => setAiCompareDialogOpen(true)}
                            sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main, '&:hover': { borderColor: theme.palette.primary.dark, bgcolor: alpha(theme.palette.primary.main, 0.04) }, textTransform: 'none', fontSize: '0.75rem' }}>
                            Compare ({selectedAiIds.size})
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
              <TablePagination
                component="div"
                count={aiTotal}
                page={aiPage}
                onPageChange={(e, newPage) => setAiPage(newPage)}
                rowsPerPage={aiRowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRows = parseInt(e.target.value, 10);
                  setAiPage(computeNewPageAfterRowsChange(aiPage, aiRowsPerPage, newRows));
                  setAiRowsPerPage(newRows);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
              />
            </Box>

            {aiLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : aiImages.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No AI generated images found</Typography></Paper>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1 }}>
                {aiImages.map((img, idx) => (
                  <Box key={img.id} onClick={() => {
                    if (aiSelectMode) { toggleAiSelect(img.id); } else { setLightboxIndex(idx); }
                  }} sx={{
                    position: 'relative', borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                    aspectRatio: '1', bgcolor: isDark ? 'grey.900' : 'grey.100',
                    border: selectedAiIds.has(img.id) ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                    '&:hover .img-overlay': { opacity: 1 },
                    contain: 'content',
                  }}>
                    {aiSelectMode && (
                      <Checkbox size="small" checked={selectedAiIds.has(img.id)}
                        onClick={(e) => { e.stopPropagation(); toggleAiSelect(img.id); }}
                        sx={{ position: 'absolute', top: 2, left: 2, zIndex: 2, p: 0, color: 'white', '&.Mui-checked': { color: theme.palette.primary.main }, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '4px', width: 24, height: 24 }} />
                    )}
                    <img
                      src={img.s3Url || getImageUrl(img.id)}
                      alt={img.promptName || ''}
                      loading="lazy"
                      decoding="async"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.target.style.opacity = 0.3; }}
                    />
                    <Box className="img-overlay" sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      p: 0.75, transition: 'opacity 0.15s',
                    }}>
                      <Typography sx={{ color: '#fff', fontSize: '0.65rem', lineHeight: 1.2 }} noWrap>{img.userEmail}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }} noWrap>
                        {img.promptName || 'Untitled'}
                        {img.model && <span style={{ marginLeft: 4, color: aiModelColor(img.model), fontWeight: 600 }}>· {aiModelLabel(img.model)}</span>}
                      </Typography>
                    </Box>
                    {img.mode && (
                      <Box sx={{ position: 'absolute', top: 4, right: 4, bgcolor: img.mode === 'Furniture' ? 'primary.main' : 'secondary.main', color: '#fff', fontSize: '0.55rem', px: 0.5, py: 0.1, borderRadius: 0.5, lineHeight: 1.3 }}>
                        {img.mode}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Lightbox */}
            <Dialog open={lightboxIndex >= 0} onClose={() => { setLightboxIndex(-1); setAdminZoomActive(false); }} maxWidth="lg" fullWidth
              PaperProps={{ sx: { bgcolor: isDark ? 'grey.900' : '#fff', maxHeight: '95vh' } }}>
              {lightboxIndex >= 0 && lightboxIndex < aiImages.length && (() => {
                const img = aiImages[lightboxIndex];
                return (
                  <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>
                    {/* Image */}
                    <Box
                      onTouchStart={handleLightboxTouchStart}
                      onTouchEnd={handleLightboxTouchEnd}
                      sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000', position: 'relative', minHeight: 400 }}
                    >
                      <Box
                        sx={{ position: 'relative', display: 'inline-block', cursor: adminZoomActive ? 'none' : 'zoom-in', overflow: 'hidden' }}
                        onClick={(e) => {
                          if (!adminZoomActive) { setAdminZoomActive(true); e.stopPropagation(); }
                          else setAdminZoomActive(false);
                        }}
                        onMouseMove={(e) => {
                          if (!adminZoomActive) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = ((e.clientX - rect.left) / rect.width) * 100;
                          const y = ((e.clientY - rect.top) / rect.height) * 100;
                          const overlay = e.currentTarget.querySelector('.admin-zoom-overlay');
                          if (overlay) {
                            overlay.style.opacity = '1';
                            overlay.style.backgroundPosition = `${x}% ${y}%`;
                          }
                        }}
                      >
                        <img src={img.s3Url || getImageUrl(img.id)} alt={img.promptName || ''} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
                        {adminZoomActive && (
                          <div className="admin-zoom-overlay" style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                            backgroundImage: `url(${img.s3Url || getImageUrl(img.id)})`,
                            backgroundSize: '250%', backgroundRepeat: 'no-repeat',
                            backgroundPosition: '50% 50%',
                            opacity: 0, pointerEvents: 'none',
                          }}
                          ref={(el) => {
                            if (!el) return;
                            const parent = el.parentElement;
                            parent.onmousemove = (e) => {
                              const rect = parent.getBoundingClientRect();
                              const x = ((e.clientX - rect.left) / rect.width) * 100;
                              const y = ((e.clientY - rect.top) / rect.height) * 100;
                              el.style.opacity = '1';
                              el.style.backgroundPosition = `${x}% ${y}%`;
                            };
                          }}
                          />
                        )}
                        {!adminZoomActive && (
                          <div style={{
                            position: 'absolute', bottom: 8, right: 8,
                            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                            padding: '4px 10px', borderRadius: 4, fontSize: '0.7rem',
                            pointerEvents: 'none',
                          }}>Click to zoom</div>
                        )}
                      </Box>
                      {/* Nav arrows */}
                      {lightboxIndex > 0 && (
                        <IconButton onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); setAdminZoomActive(false); }}
                          sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                          <ChevronLeft />
                        </IconButton>
                      )}
                      {lightboxIndex < aiImages.length - 1 && (
                        <IconButton onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); setAdminZoomActive(false); }}
                          sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                          <ChevronRight />
                        </IconButton>
                      )}
                      <IconButton onClick={() => setLightboxIndex(-1)}
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        <Close />
                      </IconButton>
                      <Typography sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                        {lightboxIndex + 1} / {aiImages.length}
                      </Typography>
                    </Box>
                    {/* Sidebar */}
                    <Box sx={{
                width: { xs: '100%', md: 300 }, p: 2, overflow: 'auto', borderLeft: { md: `1px solid ${theme.palette.divider}` },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#555' : '#bbb', borderRadius: 3 },
              }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{img.userName || img.userEmail}</Typography>
                          {img.userName && <Typography variant="caption" color="text.secondary">{img.userEmail}</Typography>}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">{img.createdAt}</Typography>
                      {img.mode && <Chip label={img.mode} size="small" color={img.mode === 'Furniture' ? 'primary' : 'secondary'} sx={{ mt: 1, fontSize: '0.7rem', height: 22 }} />}
                      {img.batchSize > 1 && <Chip label={`${img.batchSize} in batch`} size="small" variant="outlined" sx={{ mt: 1, ml: 0.5, fontSize: '0.7rem', height: 22 }} />}
                      {/* Gemini image model that produced this row. Older rows
                          pre-dating the Model column show "Unknown" so the
                          absence is visible. */}
                      {(() => {
                        const label = aiModelLabel(img.model);
                        const color = aiModelColor(img.model);
                        return (
                          <Tooltip title={img.model || 'Model not recorded — generated before the model picker shipped'} arrow disableInteractive>
                            <Chip
                              label={label}
                              size="small"
                              sx={{ mt: 1, ml: 0.5, fontSize: '0.7rem', height: 22, bgcolor: `${color}20`, color, fontWeight: 600 }}
                            />
                          </Tooltip>
                        );
                      })()}

                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Prompt</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{img.promptName || 'Untitled'}</Typography>
                      {img.promptText && (
                        <Paper variant="outlined" sx={{
                          p: 1, mb: 1, maxHeight: 200, overflow: 'auto',
                          '&::-webkit-scrollbar': { width: 6 },
                          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                          '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#555' : '#bbb', borderRadius: 3 },
                        }}>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>{img.promptText}</Typography>
                        </Paper>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                        {img.promptText && (
                          <Button size="small" variant={copiedId === img.id ? 'contained' : 'outlined'} color={copiedId === img.id ? 'success' : 'primary'}
                            startIcon={<ContentCopy sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            onClick={() => handleCopyPrompt(img.id, img.promptText)}>
                            {copiedId === img.id ? 'Copied!' : 'Copy Prompt'}
                          </Button>
                        )}
                        <Button size="small" variant="outlined" startIcon={<OpenInNew sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          onClick={() => window.open(img.s3Url || getImageUrl(img.id), '_blank')}>
                          Open
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<Download sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('jwtToken');
                              const res = await fetch(`${API_BASE_URL}/api/AIImageGenerator/admin/download-image/${img.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = img.fileName || 'image.png'; a.click();
                              URL.revokeObjectURL(url);
                            } catch { window.open(img.s3Url || getImageUrl(img.id), '_blank'); }
                          }}>
                          Download
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>{img.fileName}</Typography>
                    </Box>
                  </DialogContent>
                );
              })()}
            </Dialog>

            {aiImages.length > 0 && (
              <TablePagination
                component="div"
                count={aiTotal}
                page={aiPage}
                onPageChange={(e, newPage) => { setAiPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                rowsPerPage={aiRowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRows = parseInt(e.target.value, 10);
                  setAiPage(computeNewPageAfterRowsChange(aiPage, aiRowsPerPage, newRows));
                  setAiRowsPerPage(newRows);
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            )}
          </>
        )}

        {activeTab === 2 && (
          <>
            {/* AI Videos Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="User Email" fullWidth size="small" value={aiVideoSearchEmail} onChange={(e) => setAiVideoSearchEmail(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Search Prompts" fullWidth size="small" value={aiVideoPromptSearch} onChange={(e) => setAiVideoPromptSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort</InputLabel>
                    <Select value={aiVideoSortBy} label="Sort" onChange={(e) => setAiVideoSortBy(e.target.value)}>
                      <MenuItem value="newest">Newest</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                      <MenuItem value="user">By User</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <TextField label="From" type="date" fullWidth size="small" value={aiVideoDateFrom} onChange={(e) => setAiVideoDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1.5}>
                  <TextField label="To" type="date" fullWidth size="small" value={aiVideoDateTo} onChange={(e) => setAiVideoDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={3} md={1}>
                  <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchAiVideos} fullWidth>Refresh</Button>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                {[
                  { label: 'Today', days: 0 },
                  { label: '3 Days', days: 3 },
                  { label: '1 Week', days: 7 },
                  { label: '2 Weeks', days: 14 },
                  { label: '1 Month', days: 30 },
                  { label: '3 Months', days: 90 },
                  { label: 'All Time', days: -1 },
                ].map(({ label, days }) => {
                  const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const today = localDate(new Date());
                  const fromDate = new Date(); fromDate.setDate(fromDate.getDate() - days);
                  const from = days === -1 ? '' : days === 0 ? today : localDate(fromDate);
                  const to = days === -1 ? '' : today;
                  const isActive = days === -1 ? (!aiVideoDateFrom && !aiVideoDateTo) : (aiVideoDateFrom === from && aiVideoDateTo === to);
                  return (
                    <Chip key={label} label={label} size="small" clickable
                      onClick={() => { setAiVideoDateFrom(from); setAiVideoDateTo(to); }}
                      sx={{
                        height: 24, fontSize: '0.7rem', fontWeight: isActive ? 700 : 400,
                        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                        border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                      }}
                    />
                  );
                })}
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {aiVideoTotal.toLocaleString()} videos
              </Typography>
              <TablePagination
                component="div"
                count={aiVideoTotal}
                page={aiVideoPage}
                onPageChange={(e, newPage) => setAiVideoPage(newPage)}
                rowsPerPage={aiVideoRowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRows = parseInt(e.target.value, 10);
                  setAiVideoPage(computeNewPageAfterRowsChange(aiVideoPage, aiVideoRowsPerPage, newRows));
                  setAiVideoRowsPerPage(newRows);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
              />
            </Box>

            {aiVideoLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : aiVideos.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No AI generated videos found</Typography></Paper>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Preview</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Prompt</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Date (EST)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aiVideos.map((video, vidIdx) => (
                      <TableRow key={video.id} hover>
                        <TableCell sx={{ width: 90 }}>
                          <Box sx={{
                            width: 80, height: 56, borderRadius: 1, bgcolor: '#000', position: 'relative',
                            overflow: 'hidden', cursor: 'pointer',
                            border: `2px solid transparent`,
                            '&:hover': { borderColor: theme.palette.primary.main },
                            '&:hover .vid-play-overlay': { opacity: 1 },
                          }} onClick={() => setPlayingVideoIndex(vidIdx)}>
                            {video.s3Url ? (
                              <video src={video.s3Url} muted preload="metadata"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onLoadedData={(e) => { e.target.currentTime = 0.5; }} />
                            ) : (
                              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? '#1e1e1e' : '#f5f5f5' }}>
                                <Videocam sx={{ fontSize: 24, color: 'text.secondary' }} />
                              </Box>
                            )}
                            <Box className="vid-play-overlay" sx={{
                              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: 'rgba(0,0,0,0.4)', opacity: 0, transition: 'opacity 0.15s',
                            }}>
                              <PlayArrow sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>{video.userEmail || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          <Chip label={videoModelLabel(video.engine)} size="small"
                            sx={{ height: 20, fontSize: 10, fontWeight: 600, color: '#fff', bgcolor: videoModelColor(video.engine) }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 300 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Tooltip title={video.prompt || ''}>
                              <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                                {video.prompt || '—'}
                              </Typography>
                            </Tooltip>
                            {video.prompt && (
                              <Tooltip title={copiedId === `tbl-${video.id}` ? 'Copied!' : 'Copy prompt'}>
                                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(video.prompt); setCopiedId(`tbl-${video.id}`); setTimeout(() => setCopiedId(null), 2000); }}
                                  sx={{ flexShrink: 0, p: 0.25 }}>
                                  <ContentCopy sx={{ fontSize: 13, color: copiedId === `tbl-${video.id}` ? 'success.main' : 'text.secondary' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {video.resolution && <Chip label={video.resolution} size="small" sx={{ height: 20, fontSize: 10 }} />}
                            {video.aspectRatio && <Chip label={video.aspectRatio} size="small" sx={{ height: 20, fontSize: 10 }} />}
                            {video.duration && <Chip label={`${video.duration}s`} size="small" sx={{ height: 20, fontSize: 10 }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{video.createdAt}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {video.s3Url && (
                              <Tooltip title="Download video">
                                <IconButton size="small" onClick={() => {
                                  const a = document.createElement('a');
                                  a.href = video.s3Url;
                                  a.download = video.videoFileName || 'video.mp4';
                                  a.target = '_blank';
                                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                }}>
                                  <Download sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {aiVideoTotal > aiVideoRowsPerPage && (
              <TablePagination
                component="div" count={aiVideoTotal} page={aiVideoPage}
                onPageChange={(e, newPage) => { setAiVideoPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                rowsPerPage={aiVideoRowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRows = parseInt(e.target.value, 10);
                  setAiVideoPage(computeNewPageAfterRowsChange(aiVideoPage, aiVideoRowsPerPage, newRows));
                  setAiVideoRowsPerPage(newRows);
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            )}

            {/* Video Player Dialog with prev/next navigation */}
            <Dialog open={playingVideoIndex >= 0} onClose={() => setPlayingVideoIndex(-1)} maxWidth="md" fullWidth
              PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}>
              <DialogContent sx={{ p: 0, position: 'relative' }}>
                {/* Close button */}
                <IconButton onClick={() => setPlayingVideoIndex(-1)}
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                  <Close />
                </IconButton>

                {/* Previous button */}
                {playingVideoIndex > 0 && (
                  <IconButton onClick={() => setPlayingVideoIndex(prev => prev - 1)}
                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                    <ChevronLeft sx={{ fontSize: 32 }} />
                  </IconButton>
                )}

                {/* Next button */}
                {playingVideoIndex < aiVideos.length - 1 && (
                  <IconButton onClick={() => setPlayingVideoIndex(prev => prev + 1)}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                    <ChevronRight sx={{ fontSize: 32 }} />
                  </IconButton>
                )}

                {/* Video */}
                {playingVideoIndex >= 0 && aiVideos[playingVideoIndex] && (
                  <video key={aiVideos[playingVideoIndex].id} src={aiVideos[playingVideoIndex].s3Url} controls autoPlay
                    style={{ width: '100%', maxHeight: '80vh', display: 'block' }} />
                )}

                {/* Info bar */}
                {playingVideoIndex >= 0 && aiVideos[playingVideoIndex] && (
                  <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>
                        {aiVideos[playingVideoIndex].userEmail} — {playingVideoIndex + 1} of {aiVideos.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#fff', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4, cursor: 'pointer', '&:hover': { color: '#ccc' } }}
                        onClick={() => { navigator.clipboard.writeText(aiVideos[playingVideoIndex].prompt || ''); setCopiedId(`vid-${aiVideos[playingVideoIndex].id}`); setTimeout(() => setCopiedId(null), 2000); }}>
                        {copiedId === `vid-${aiVideos[playingVideoIndex].id}` ? '✓ Copied!' : (aiVideos[playingVideoIndex].prompt || 'No prompt')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                      <Chip label={videoModelLabel(aiVideos[playingVideoIndex].engine)} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: videoModelColor(aiVideos[playingVideoIndex].engine), color: '#fff' }} />
                      {aiVideos[playingVideoIndex].resolution && <Chip label={aiVideos[playingVideoIndex].resolution} size="small" sx={{ height: 22, fontSize: 10, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />}
                      {aiVideos[playingVideoIndex].aspectRatio && <Chip label={aiVideos[playingVideoIndex].aspectRatio} size="small" sx={{ height: 22, fontSize: 10, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />}
                      {aiVideos[playingVideoIndex].duration && <Chip label={`${aiVideos[playingVideoIndex].duration}s`} size="small" sx={{ height: 22, fontSize: 10, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />}
                    </Box>
                  </Box>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}

        {activeTab === 3 && (
          <>
            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" label="User Email"
                    value={emailTemplateSearchEmail}
                    onChange={(e) => { setEmailTemplateSearchEmail(e.target.value); setEmailTemplatesPage(0); }}
                    InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth size="small" label="Search (name / subject / prompt)"
                    value={emailTemplateSearch}
                    onChange={(e) => { setEmailTemplateSearch(e.target.value); setEmailTemplatesPage(0); }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Campaign Type</InputLabel>
                    <Select
                      label="Campaign Type"
                      value={emailTemplateCampaignType}
                      onChange={(e) => { setEmailTemplateCampaignType(e.target.value); setEmailTemplatesPage(0); }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="urgency-flash-sale">Flash Sale</MenuItem>
                      <MenuItem value="social-proof-trending">Social Proof</MenuItem>
                      <MenuItem value="room-transformation">Room Transform</MenuItem>
                      <MenuItem value="designer-curated">Designer Curated</MenuItem>
                      <MenuItem value="new-arrivals-first-look">First Look</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                    value={emailTemplateDateFrom}
                    onChange={(e) => { setEmailTemplateDateFrom(e.target.value); setEmailTemplatesPage(0); }}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                    value={emailTemplateDateTo}
                    onChange={(e) => { setEmailTemplateDateTo(e.target.value); setEmailTemplatesPage(0); }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={fetchEmailTemplates}>
                  Refresh
                </Button>
              </Box>
            </Paper>

            {/* Table */}
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isDark ? theme.palette.grey[800] : theme.palette.grey[100] }}>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Items</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Discount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created (EST)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Preview</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailTemplatesLoading ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} /></TableCell></TableRow>
                    ) : emailTemplates.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No email campaigns found</TableCell></TableRow>
                    ) : (
                      emailTemplates.map((t) => (
                        <TableRow key={t.id} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{t.userEmail}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t.isFavorite && <Star sx={{ fontSize: 14, color: '#f59e0b' }} />}
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {t.campaignType && <Chip label={t.campaignType.replace(/-/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 280, color: 'text.secondary' }}>
                              {t.subject || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{t.itemCount}</TableCell>
                          <TableCell align="center">
                            {t.discountPercent != null ? (
                              <Chip label={`${t.discountPercent}%`} size="small" color="error" sx={{ height: 20 }} />
                            ) : '—'}
                          </TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{t.createdAt}</Typography></TableCell>
                          <TableCell align="right">
                            <Tooltip title="Preview email">
                              <IconButton size="small" onClick={() => openEmailTemplatePreview(t.id)}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={emailTemplatesTotal}
                page={emailTemplatesPage}
                onPageChange={(e, p) => setEmailTemplatesPage(p)}
                rowsPerPage={emailTemplatesRowsPerPage}
                onRowsPerPageChange={(e) => {
                  const newRows = parseInt(e.target.value, 10);
                  setEmailTemplatesPage(computeNewPageAfterRowsChange(emailTemplatesPage, emailTemplatesRowsPerPage, newRows));
                  setEmailTemplatesRowsPerPage(newRows);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>

            {/* Preview dialog */}
            <Dialog
              open={!!emailTemplatePreview}
              onClose={() => setEmailTemplatePreview(null)}
              maxWidth="lg"
              fullWidth
              onKeyDown={handleEmailPreviewKeyDown}
              PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}
            >
              {emailTemplatePreview && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {emailTemplatePreview.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {emailTemplatePreviewIndex >= 0 && (
                          <Box component="span" sx={{ mr: 1 }}>
                            {emailTemplatePreviewIndex + 1} of {emailTemplates.length}
                          </Box>
                        )}
                        {emailTemplatePreview.campaignType} · {emailTemplatePreview.createdAt}
                        {emailTemplatePreview.subject ? ` · Subject: ${emailTemplatePreview.subject}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      <Tooltip title="Previous (← arrow)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={emailTemplatePreviewIndex <= 0}
                            onClick={previewPrevTemplate}
                          >
                            <ChevronLeft />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Next (→ arrow)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={emailTemplatePreviewIndex < 0 || emailTemplatePreviewIndex >= emailTemplates.length - 1}
                            onClick={previewNextTemplate}
                          >
                            <ChevronRight />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button size="small" variant={emailTemplatePreviewMode === 'preview' ? 'contained' : 'outlined'} onClick={() => setEmailTemplatePreviewMode('preview')}>
                        Preview
                      </Button>
                      <Button size="small" variant={emailTemplatePreviewMode === 'mjml' ? 'contained' : 'outlined'} onClick={() => setEmailTemplatePreviewMode('mjml')} startIcon={<Code />}>
                        MJML
                      </Button>
                      <Button size="small" variant={emailTemplatePreviewMode === 'html' ? 'contained' : 'outlined'} onClick={() => setEmailTemplatePreviewMode('html')}>
                        HTML
                      </Button>
                      <IconButton size="small" onClick={() => setEmailTemplatePreview(null)}><Close fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <DialogContent
                    dividers
                    sx={{
                      p: 0,
                      bgcolor: emailTemplatePreviewMode === 'preview' ? '#f5f5f5' : 'background.default',
                      flex: 1,
                      overflow: 'hidden',
                      minHeight: 500,
                      position: 'relative',
                    }}
                  >
                    {emailTemplatePreviewMode === 'preview' ? (
                      emailTemplatePreview.html ? (
                        <iframe
                          key={emailTemplatePreview.id}
                          srcDoc={emailTemplatePreview.html}
                          title="Email preview"
                          style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            width: '100%', height: '100%',
                            border: 'none', display: 'block',
                            backgroundColor: '#fff',
                          }}
                        />
                      ) : (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', p: 4 }}>
                          No HTML available for preview.
                        </Box>
                      )
                    ) : (
                      <Box component="pre" sx={{
                        m: 0, p: 2, fontSize: 12, lineHeight: 1.5,
                        position: 'absolute', inset: 0, overflow: 'auto',
                        fontFamily: 'Menlo, Monaco, monospace',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {emailTemplatePreviewMode === 'mjml' ? (emailTemplatePreview.mjml || '(no MJML stored)') : (emailTemplatePreview.html || '(no HTML stored)')}
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
                      Tip: use ← / → arrow keys to jump between emails
                    </Typography>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(emailTemplatePreviewMode === 'mjml' ? (emailTemplatePreview.mjml || '') : (emailTemplatePreview.html || ''));
                      }}
                      startIcon={<ContentCopy />}
                    >
                      Copy {emailTemplatePreviewMode === 'mjml' ? 'MJML' : 'HTML'}
                    </Button>
                    <Button onClick={() => setEmailTemplatePreview(null)}>Close</Button>
                  </DialogActions>
                </>
              )}
            </Dialog>
          </>
        )}

        {activeTab === 0 && <>
        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="User Email"
                fullWidth
                size="small"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Interface"
                fullWidth
                size="small"
                value={searchInterface}
                onChange={(e) => setSearchInterface(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="From"
                type="date"
                fullWidth
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input': { colorScheme: isDark ? 'dark' : 'light' },
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                label="To"
                type="date"
                fullWidth
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input': { colorScheme: isDark ? 'dark' : 'light' },
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  label="Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="upload">Uploads</MenuItem>
                  <MenuItem value="download">Downloads</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSearch}>
                  Search
                </Button>
                <Button variant="outlined" size="small" onClick={handleClearFilters}>
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
            {[
              { label: 'Today', days: 0 },
              { label: '3 Days', days: 3 },
              { label: '1 Week', days: 7 },
              { label: '2 Weeks', days: 14 },
              { label: '1 Month', days: 30 },
              { label: '3 Months', days: 90 },
              { label: 'All Time', days: -1 },
            ].map(({ label, days }) => {
              const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const today = localDate(new Date());
              const fromDate = new Date(); fromDate.setDate(fromDate.getDate() - days);
              const from = days === -1 ? '' : days === 0 ? today : localDate(fromDate);
              const to = days === -1 ? '' : today;
              const isActive = days === -1 ? (!dateFrom && !dateTo) : (dateFrom === from && dateTo === to);
              return (
                <Chip key={label} label={label} size="small" clickable
                  onClick={() => { setDateFrom(from); setDateTo(to); }}
                  sx={{
                    height: 24, fontSize: '0.7rem', fontWeight: isActive ? 700 : 400,
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                    border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                />
              );
            })}
          </Box>
        </Paper>

        {/* Actions row: selection actions + export + refresh, all together on the right */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
          {selectedIds.size > 0 && (
            <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleBulkDownload} color="primary">
              {`Download Selected (${selectedIds.size})`}
            </Button>
          )}
          {selectedIds.size > 0 && getCompareImages().length >= 2 && (
            <Button variant="outlined" size="small" startIcon={<ImageIcon />} onClick={() => setCompareDialogOpen(true)}
              sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main, '&:hover': { borderColor: theme.palette.primary.dark, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
              {`Compare Images (${getCompareImages().length})`}
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<FileDownload />} onClick={handleExportExcel} disabled={history.length === 0}>
            Export to Excel
          </Button>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchHistory}>
            Refresh
          </Button>
        </Box>

        {/* Total records + Pagination row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {total.toLocaleString()} total records
          </Typography>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newRows = parseInt(e.target.value, 10);
              setPage(computeNewPageAfterRowsChange(page, rowsPerPage, newRows));
              setRowsPerPage(newRows);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            sx={{ '& .MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }}
          />
        </Box>

        {/* Table */}
        <TableContainer component={Paper} elevation={2} sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 8, width: 8 },
          '&::-webkit-scrollbar-track': { bgcolor: isDark ? '#1e1e2e' : '#f5f5f5' },
          '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#555' : '#bbb', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: isDark ? '#777' : '#999' },
        }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: isDark
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                }}
              >
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={isAllSelected}
                    indeterminate={
                      selectedIds.size > 0 && !isAllSelected && selectableOnPage.some((h) => selectedIds.has(h.id))
                    }
                    onChange={toggleSelectAll}
                    disabled={selectableOnPage.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, position: 'relative', width: fileNameWidth, minWidth: 80 }}>
                  File Name
                  <Box sx={resizeHandleSx} onMouseDown={(e) => startResize(e, setFileNameWidth, fileNameWidth)} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, position: 'relative', width: interfaceWidth, minWidth: 80 }}>
                  Interface
                  <Box sx={resizeHandleSx} onMouseDown={(e) => startResize(e, setInterfaceWidth, interfaceWidth)} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date (EST)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((h, index) => {
                  const isDownload = h.upload === true;
                  const isBatchRow = h.link && h.link.startsWith('batch:');
                  const batchId = isBatchRow ? h.link.replace('batch:', '') : null;
                  const batchImageList = batchId && batchImages[batchId] ? batchImages[batchId] : [];
                  const isExpanded = isBatchRow && expandedBatchRows.has(h.id);
                  const toggleExpanded = () => {
                    setExpandedBatchRows((prev) => {
                      const next = new Set(prev);
                      if (next.has(h.id)) next.delete(h.id);
                      else next.add(h.id);
                      return next;
                    });
                  };
                  return (
                    <React.Fragment key={h.id || index}>
                    <TableRow
                      hover
                      sx={{
                        bgcolor: selectedIds.has(h.id)
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'inherit',
                      }}
                    >
                      <TableCell padding="checkbox">
                        {isBatchRow ? (
                          (() => {
                            const subIds = batchImageList.map((_, i) => `batch:${batchId}:${i}`);
                            const allSubSelected = subIds.length > 0 && subIds.every((sid) => selectedIds.has(sid));
                            const someSubSelected = subIds.some((sid) => selectedIds.has(sid));
                            return (
                              <Checkbox
                                size="small"
                                color="primary"
                                disabled={subIds.length === 0}
                                checked={allSubSelected}
                                indeterminate={!allSubSelected && someSubSelected}
                                onChange={() => toggleBatchAll(batchId)}
                              />
                            );
                          })()
                        ) : h.link ? (
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={selectedIds.has(h.id)}
                            onChange={() => toggleSelect(h.id)}
                          />
                        ) : (
                          <Checkbox size="small" disabled />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {h.userEmailId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={h.filename}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: fileNameWidth }}>
                            {h.filename}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const path = resolveInterfacePath(h.interfaceName);
                          if (!h.interfaceName) return null;
                          if (!path) {
                            return (
                              <Typography variant="body2" noWrap sx={{ maxWidth: interfaceWidth }}>
                                {h.interfaceName}
                              </Typography>
                            );
                          }
                          return (
                            <Tooltip title={`Open ${h.interfaceName}`}>
                              <Typography
                                component="a"
                                href={path}
                                onClick={(e) => {
                                  // Plain click → SPA navigation; modifier-click
                                  // (cmd/ctrl/middle) lets the browser open in
                                  // a new tab via the href.
                                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                  e.preventDefault();
                                  navigate(path);
                                }}
                                variant="body2"
                                noWrap
                                sx={{
                                  maxWidth: interfaceWidth,
                                  display: 'inline-block',
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {h.interfaceName}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={isDownload ? <Download sx={{ fontSize: 14 }} /> : <Upload sx={{ fontSize: 14 }} />}
                          label={isDownload ? 'Download' : 'Upload'}
                          size="small"
                          color={isDownload ? 'primary' : 'success'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                          {h.createdAt}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {h.link && !h.link.startsWith('batch:') && (
                          <Tooltip title="Open link">
                            <IconButton
                              size="small"
                              onClick={() => window.open(h.link, '_blank')}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isBatchRow && (
                          <Chip
                            label={`Batch API${batchImageList.length > 0 ? ` (${batchImageList.length})` : ''}`}
                            size="small"
                            onClick={batchImageList.length > 0 ? toggleExpanded : undefined}
                            icon={
                              batchImageList.length > 0
                                ? isExpanded
                                  ? <KeyboardArrowUp sx={{ fontSize: 14, color: '#f59e0b !important' }} />
                                  : <KeyboardArrowDown sx={{ fontSize: 14, color: '#f59e0b !important' }} />
                                : undefined
                            }
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              fontWeight: 600,
                              bgcolor: '#f59e0b20',
                              color: '#f59e0b',
                              border: '1px solid #f59e0b',
                              cursor: batchImageList.length > 0 ? 'pointer' : 'default',
                              '&:hover': batchImageList.length > 0 ? { bgcolor: '#f59e0b30' } : undefined,
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                    {isBatchRow && batchImageList.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: 0, bgcolor: alpha('#f59e0b', isDark ? 0.04 : 0.03) }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5 }}>
                              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: '#f59e0b', fontWeight: 600 }}>
                                {batchImageList.length} generated image{batchImageList.length !== 1 ? 's' : ''}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {batchImageList.map((img, i) => {
                                  const subId = `batch:${batchId}:${i}`;
                                  const subSelected = selectedIds.has(subId);
                                  return (
                                  <Box
                                    key={i}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      bgcolor: subSelected
                                        ? alpha(theme.palette.primary.main, 0.12)
                                        : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                      '&:hover': { bgcolor: subSelected
                                        ? alpha(theme.palette.primary.main, 0.16)
                                        : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      color="primary"
                                      checked={subSelected}
                                      onChange={() => toggleSelect(subId)}
                                      sx={{ p: 0.25 }}
                                    />
                                    <Typography variant="caption" sx={{ minWidth: 24, color: 'text.secondary', fontWeight: 600 }}>
                                      {i + 1}.
                                    </Typography>
                                    <Tooltip title={img.fileName || ''}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          flex: 1,
                                          fontSize: '0.75rem',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {img.fileName || img.s3Url}
                                      </Typography>
                                    </Tooltip>
                                    <Tooltip title="Copy URL">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          navigator.clipboard.writeText(img.s3Url || '');
                                        }}
                                      >
                                        <ContentCopy sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open link">
                                      <IconButton
                                        size="small"
                                        onClick={() => window.open(img.s3Url, '_blank')}
                                      >
                                        <OpenInNew sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newRows = parseInt(e.target.value, 10);
              setPage(computeNewPageAfterRowsChange(page, rowsPerPage, newRows));
              setRowsPerPage(newRows);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </TableContainer>
        </>}

        {/* Image Comparison Dialog */}
        <Dialog open={compareDialogOpen} onClose={() => setCompareDialogOpen(false)} maxWidth="xl" fullWidth
          PaperProps={{ sx: { minHeight: '60vh' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Image Comparison ({getCompareImages().length} images)
            </Typography>
            <IconButton onClick={() => setCompareDialogOpen(false)}><Close /></IconButton>
          </Box>
          <DialogContent sx={{ p: 2 }}>
            {(() => {
              const images = getCompareImages();
              const cols = images.length <= 2 ? 2 : images.length <= 4 ? images.length : 4;
              return (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, 1fr)`,
                  gap: 2,
                }}>
                  {images.map((h) => (
                    <Paper key={h.id} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
                      <Box component="img" src={h.link} alt={h.filename}
                        sx={{ width: '100%', aspectRatio: '1', objectFit: 'contain', bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#f5f5f5', display: 'block' }}
                        onError={(e) => { e.target.alt = 'Failed to load'; }} />
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, mb: 0.3 }} noWrap>{h.filename}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{h.interfaceName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>{h.userEmailId} — {h.createdAt}</Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* AI Image Comparison Dialog */}
        {aiCompareDialogOpen && (
          <Dialog open={true} onClose={() => setAiCompareDialogOpen(false)}
            maxWidth="xl" fullWidth
            sx={{ zIndex: 1400 }}
            PaperProps={{ sx: { minHeight: '70vh', maxHeight: '95vh' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: `2px solid ${theme.palette.primary.main}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Image Comparison</Typography>
                <Chip label={`${getSelectedAiImages().length} images`} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }} />
              </Box>
              <IconButton onClick={() => setAiCompareDialogOpen(false)}><Close /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, overflow: 'auto' }}>
              {(() => {
                const images = getSelectedAiImages();
                if (images.length === 0) return <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No images selected. Close this dialog, select images, then compare.</Typography>;
                const cols = images.length === 2 ? 2 : images.length === 3 ? 3 : 2;
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${cols}, 1fr)` }, gap: 3, mt: 1 }}>
                    {images.map((img) => (
                      <Box key={img.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: `2px solid ${theme.palette.primary.main}`, cursor: 'zoom-in' }}
                          onClick={() => {
                            setAiCompareDialogOpen(false);
                            setTimeout(() => {
                              const idx = aiImages.findIndex(a => a.id === img.id);
                              if (idx >= 0) setLightboxIndex(idx);
                            }, 200);
                          }}>
                          <img src={img.s3Url || getImageUrl(img.id)} alt={img.fileName || img.promptName}
                            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.target.style.opacity = 0.3; }} />
                          <Box sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem' }}>
                            Click to zoom
                          </Box>
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                          {img.fileName || img.promptName || 'Untitled'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {img.mode && <Chip label={img.mode} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
                          <Chip label={img.userEmail} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                          <Chip label={new Date(img.createdAt).toLocaleDateString()} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                        </Box>
                        {img.promptName && (
                          <AiComparePrompt promptName={img.promptName} promptText={img.promptText} isDark={isDark} />
                        )}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </AdminGuard>
  );
};

const AiComparePrompt = ({ promptName, promptText, isDark }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        Prompt: {promptName}
      </Typography>
      {promptText && (
        <>
          <Box sx={{ mt: 0.5 }}>
            <Button size="small" onClick={() => setExpanded(!expanded)}
              sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'primary.main', p: 0, minWidth: 0 }}>
              {expanded ? '▲ Hide prompt' : '▼ Show prompt'}
            </Button>
          </Box>
          {expanded && (
            <Box sx={{ mt: 0.5, p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 1, maxHeight: 200, overflow: 'auto', textAlign: 'left' }}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {promptText}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FileHistoryAdmin;
